import { createAction } from "@reduxjs/toolkit";
import { isAfter } from "date-fns";

import {
  AssessmentSettingsFragment,
  WorkSettingsFragment,
} from "@/generated/graphql";

/** Action payload for the updateSheet action. */
export interface HydrateSheetPayload {
  /** When the Sheet was released. */
  sheetReleaseTimestamp: string | null;
  /** When the next sheet will be released. */
  sheetNextReleaseTimestamp: string | null;
}

/**
 * This action is used to synchronise Sheet Requirements.
 */
export const updateSheet = createAction<HydrateSheetPayload>(
  "assignment/updateSheet"
);

/**
 * Action for sync Assessment Settings up with Redux store.
 */
export const updateAssessmentSettings = createAction<{
  settings: AssessmentSettingsFragment;
}>("assignment/updateAssessmentSettings");

/**
 * Action for sync Work Settings up with Redux store. Requires latest Assessment
 * Settings for settings important dates correctly.
 */
export const updateWorkTimeline = createAction<{
  settings: AssessmentSettingsFragment;
  /** Settings applied directly for the student's work. */
  workSettings: WorkSettingsFragment | null;
  /** Date when the work was manually started. */
  workStartDate: string | null;
  /** If the work belongs to the group */
  isGroupWork: boolean;
}>("assignment/updateWorkTimeline");

interface CombinedSettings {
  settings: Partial<AssessmentSettingsFragment>;
  workSettings: Partial<WorkSettingsFragment> | null;
}

/**
 * Select a assignment time limit set for the Work.
 *
 * This time limit can come from the Sheet or the Work's Settings (extension).
 * If both are present, the greater is taken.
 *
 * @param payload action payload
 * @return selected time limit and whether it was an extension or sheet's
 *   version.
 */
export function getTimeLimit(
  payload: CombinedSettings
): [number | null, boolean] {
  const { settings, workSettings } = payload;

  const assmTimeLimit =
    settings.timeLimitEnabled && typeof settings.timeLimit === "number"
      ? settings.timeLimit
      : null;

  const workTimeLimit = workSettings?.timeLimit ?? null;
  return selectTimeOrExtension(assmTimeLimit, workTimeLimit);
}

/**
 * Select a final due date for the Work.
 *
 * This date can come from the Sheet or the Work's Settings (extension),
 * whichever is later.
 *
 * @param payload action payload
 * @return selected due date and whether it was an extension or sheet's
 *   version.
 */
export function getFinalDate(
  payload: CombinedSettings & { isGroupWork?: boolean }
): [string | null, boolean] {
  const { isGroupWork = false, settings, workSettings } = payload;
  const workFinalDate = workSettings?.dueDate ?? null;

  let assmFinalDate = null;
  if (settings.groupAssessmentEnabled && !isGroupWork) {
    assmFinalDate =
      settings.individualReportEnabled &&
      typeof settings.individualReportDueDate === "string"
        ? settings.individualReportDueDate
        : (settings.dueDate ?? null);
  } else {
    assmFinalDate = settings?.dueDate ?? null;
  }

  return selectDateOrExtension(assmFinalDate, workFinalDate);
}

/**
 * Select an exam start date for a Work.
 *
 * This date can come from the Sheet or the Work's Settings (if Special
 * Consideration is provided to the student). The Special Consideration date
 * takes precedence.
 *
 * @param payload action payload
 * @return selected date and whether it's a sheet date (false) or a special
 *   consideration (true).
 */
export function getExamStartDate(
  payload: CombinedSettings
): [string | null, boolean] {
  const { settings, workSettings } = payload;
  const assmStartDate = settings.examStartDate ?? null;
  const specialStartDate = workSettings?.examStartDate ?? null;
  return [specialStartDate || assmStartDate, specialStartDate !== null];
}

/**
 * Select an exam end date for a Work.
 *
 * This date can come from the Sheet or the Work's Settings (if Special
 * Consideration is provided to the student). The Special Consideration date
 * takes precedence.
 *
 * @param payload action payload
 * @return selected date and whether it's a sheet date (false) or a special
 *   consideration (true).
 */
export function getExamEndDate(
  payload: CombinedSettings
): [string | null, boolean] {
  const { settings, workSettings } = payload;
  const assmEndDate = settings.examEndDate ?? null;
  const specialEndDate = workSettings?.examEndDate ?? null;
  return [specialEndDate || assmEndDate, specialEndDate !== null];
}

/**
 * Select a reading time set for a Work.
 *
 * This value can come from the Sheet or the Work's Settings (if Special
 * Consideration is provided to the student). The Special Consideration value
 * takes precedence.
 *
 * @param payload action payload
 * @return selected time value and whether it's from the sheet (false) or a
 *   special consideration (true).
 */
export function getExamReadingTime(
  payload: CombinedSettings
): [number | null, boolean] {
  const { settings, workSettings } = payload;
  const assmReadingTime =
    settings.examReadingTimeDisabled === false &&
    typeof settings.examReadingTime === "number"
      ? settings.examReadingTime
      : null;
  const specialReadingTime = workSettings?.examReadingTime ?? null;
  return [specialReadingTime ?? assmReadingTime, specialReadingTime !== null];
}

/**
 * Select a writing time set for a Work.
 *
 * This value can come from the Sheet or the Work's Settings (if Special
 * Consideration is provided to the student). The Special Consideration value
 * takes precedence.
 *
 * @param payload action payload
 * @return selected time value and whether it's from the sheet (false) or a
 *   special consideration (true).
 */
export function getExamWritingTime(
  payload: CombinedSettings
): [number | null, boolean] {
  const { settings, workSettings } = payload;
  const assmWritingTime = settings.examWritingTime ?? null;
  const specialWritingTime = workSettings?.examWritingTime ?? null;
  return [specialWritingTime ?? assmWritingTime, specialWritingTime !== null];
}

function selectDateOrExtension(
  assmDateStr: string | null,
  workDateStr: string | null
): [string | null, boolean] {
  if (assmDateStr !== null && workDateStr !== null) {
    const validExtension = isAfter(
      new Date(workDateStr),
      new Date(assmDateStr)
    );
    const date = validExtension ? workDateStr : assmDateStr;
    return [date, validExtension];
  }

  if (assmDateStr !== null) {
    return [assmDateStr, false];
  }

  return [null, false];
}

function selectTimeOrExtension(
  assmMinutes: number | null,
  workMinutes: number | null
): [number | null, boolean] {
  const validExtension =
    assmMinutes !== null
      ? workMinutes !== null && workMinutes > assmMinutes
      : false;
  const time = validExtension ? workMinutes : assmMinutes;
  return [time, validExtension];
}
